import React, { useState, useEffect } from 'react';
import { Check, X, Sparkles, Crown, Heart, ArrowRight, Loader, Users, Gift } from 'lucide-react';
import { Header } from '../layout/Header';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { stripeService } from '../services/StripeService';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';

export const SubscriptionPage = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const { user, families } = useAuth();
  const { showToast, ToastContainer } = useToast();
  
  const isGrandparent = (user?.role ?? 'parent') !== 'parent';

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const data = await stripeService.getSubscriptionStatus();
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planType) => {
    setProcessingPlan(planType);
    try {
      await stripeService.createCheckoutSession(planType);
    } catch (error) {
      alert(`Error: ${error.message}`);
      setProcessingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      await stripeService.openCustomerPortal();
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fce7f3 0%, #dbeafe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={48} style={{ color: '#db2777', animation: 'spin 1s linear infinite' }} />
          <p style={{ fontSize: '1.25rem', color: '#6b7280', marginTop: '1rem' }}>Loading subscription details...</p>
        </div>
      </div>
    );
  }

  const currentPlan = subscription?.plan_type || 'free';

  // Parent Plans
  const parentPlans = [
    {
      name: 'Free',
      price: 0,
      period: '/month',
      planType: 'free',
      icon: <Heart size={32} />,
      gradient: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
      features: [
        { text: 'Unlimited manual recordings', included: true },
        { text: 'Grandparents record videos', included: true },
        { text: 'Share with family', included: true },
        { text: 'AI story generation', included: false },
        { text: 'Premium library access', included: false }
      ],
      description: 'Perfect for getting started with family storytelling'
    },
    {
      name: 'Premium',
      price: 15,
      period: '/month',
      planType: 'premium',
      icon: <Sparkles size={32} />,
      gradient: 'linear-gradient(135deg, #db2777 0%, #be185d 100%)',
      features: [
        { text: 'Everything in Free', included: true },
        { text: '5 AI stories per month', included: true, highlight: true },
        { text: '5 story tokens per month', included: true },
        { text: "Grandparent's AI voice clone", included: true },
        { text: 'Access to premium library', included: true }
      ],
      description: 'Ideal for small families wanting AI-powered stories'
    },
    {
      name: 'Gold',
      price: 30,
      period: '/month',
      planType: 'gold',
      icon: <Crown size={32} />,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      popular: true,
      features: [
        { text: 'Everything in Premium', included: true },
        { text: '20 AI stories per month', included: true },
        { text: '15 story tokens per month', included: true, highlight: true },
        { text: 'Access to premium library', included: true },
        { text: 'Early access to new features', included: true }
      ],
      description: 'Maximum storytelling power for growing families'
    }
  ];

  // Grandparent Plans
  const grandparentPlans = [
    {
      name: 'Free',
      price: 0,
      period: '/month',
      planType: 'free',
      icon: <Heart size={32} />,
      gradient: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
      features: [
        { text: 'Unlimited manual recordings', included: true },
        { text: 'Record videos for grandchildren', included: true },
        { text: 'Share tokens per month', included: false },
        { text: 'Premium library access', included: false },
        { text: 'Share to premium parents', included: false }
      ],
      description: 'Start recording stories for your grandchildren'
    },
    {
      name: 'Premium',
      price: 15,
      period: '/month',
      planType: 'premium',
      icon: <Users size={32} />,
      gradient: 'linear-gradient(135deg, #db2777 0%, #be185d 100%)',
      features: [
        { text: 'Everything in Free', included: true },
        { text: '10 share tokens per month', included: true, highlight: true },
        { text: 'Share to Premium parents only', included: true },
        { text: 'Access to premium library', included: true },
        { text: 'Record premium stories', included: true }
      ],
      description: 'Share premium stories with Premium parents'
    },
    {
      name: 'Gold',
      price: 30,
      period: '/month',
      planType: 'gold',
      icon: <Gift size={32} />,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      popular: true,
      features: [
        { text: 'Everything in Premium', included: true },
        { text: 'Unlimited share tokens', included: true, highlight: true },
        { text: 'Share to ALL parents (Free/Premium/Gold)', included: true },
        { text: '5 story tokens per month', included: true },
        { text: 'Early access to new features', included: true }
      ],
      description: 'Share unlimited premium stories with all families'
    }
  ];

  const plans = isGrandparent ? grandparentPlans : parentPlans;

  const parentFaqs = [
    {
      question: 'How does AI voice generation work?',
      answer: "Grandparents create a voice clone from their existing recordings. You can then generate new stories in the grandparent's voice for any book in our library."
    },
    {
      question: 'What happens if I reach my monthly limit?',
      answer: "You can still use manual recordings! Your grandparents can record as many videos as they want. AI generation will be available again next month."
    },
    {
      question: 'Can I cancel anytime?',
      answer: "Yes! You can cancel your subscription at any time. You'll keep access until the end of your billing period."
    },
    {
      question: 'Do unused AI stories roll over?',
      answer: 'No, AI generation credits reset each month. But manual recordings are always unlimited!'
    }
  ];

  const grandparentFaqs = [
    {
      question: 'What are share tokens?',
      answer: "Share tokens allow you to share premium library stories with parents. Each story you share from the premium library uses one share token."
    },
    {
      question: 'Who can I share premium stories with?',
      answer: "Premium members can share with Premium parents only. Gold members can share with ALL parents (Free, Premium, and Gold) at no cost to them!"
    },
    {
      question: 'What are story tokens?',
      answer: "Story tokens (available in Gold plan) let you generate AI-powered stories. Gold grandparents get 5 story tokens per month to create new stories."
    },
    {
      question: 'Can I cancel anytime?',
      answer: "Yes! You can cancel your subscription at any time. You'll keep access until the end of your billing period."
    }
  ];

  const faqs = isGrandparent ? grandparentFaqs : parentFaqs;

  // Usage display for current plan
  const renderUsageDisplay = () => {
    if (currentPlan === 'free') return null;

    if (isGrandparent) {
      // Show share tokens for grandparents
      return (
        <div>
          <p style={{ marginBottom: '0.75rem', fontSize: '0.875rem' }}>
            Share Tokens: <strong>
              {subscription.share_tokens_remaining === 999999 
                ? 'Unlimited' 
                : `${subscription.share_tokens_remaining} remaining`}
            </strong>
          </p>
          {subscription.plan_type === 'gold' && (
            <p style={{ fontSize: '0.875rem' }}>
              Story Tokens: <strong>{subscription.story_tokens_remaining || 0} remaining</strong>
            </p>
          )}
        </div>
      );
    } else {
      // Show AI generation for parents
      const usagePercent = subscription?.ai_generations_limit > 0
        ? Math.min((subscription.ai_generations_used / subscription.ai_generations_limit) * 100, 100)
        : 0;

      return (
        <div>
          <p style={{ marginBottom: '0.75rem', fontSize: '0.875rem' }}>
            AI Stories Used This Month: <strong>{subscription.ai_generations_used} / {subscription.ai_generations_limit}</strong>
          </p>
          <div style={{ 
            width: '100%', 
            height: '8px', 
            background: 'rgba(255, 255, 255, 0.2)', 
            borderRadius: '9999px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              height: '100%', 
              width: `${usagePercent}%`, 
              background: usagePercent > 80 ? '#f59e0b' : 'white',
              borderRadius: '9999px',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <p style={{ fontSize: '0.875rem', marginTop: '0.75rem' }}>
            Story Tokens: <strong>{subscription.story_tokens_remaining || 0} remaining</strong>
          </p>
        </div>
      );
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fce7f3 0%, #dbeafe 100%)' }}>
      <ToastContainer />
      <Header userName={user ? `${user.first_name} ${user.last_name}` : 'User'} userType={isGrandparent ? 'reader' : 'parent'} />
      
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        {/* Hero Section */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ fontSize: '3rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.75rem' }}>
            {isGrandparent ? 'Grandparent Plans' : 'Parent Plans'}
          </h1>
          <p style={{ fontSize: '1.25rem', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
            {isGrandparent 
              ? 'Share your stories and connect with your grandchildren'
              : 'Choose the perfect plan for your family\'s storytelling needs'}
          </p>
        </div>

        {/* Current Plan Status */}
        {currentPlan !== 'free' && subscription && (
          <Card style={{ 
            background: 'linear-gradient(90deg, #059669 0%, #047857 100%)', 
            color: 'white', 
            marginBottom: '3rem', 
            padding: '2rem' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1.5rem' }}>
              <div style={{ flex: 1, minWidth: '300px' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Current Plan: {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
                </h3>
                <p style={{ color: '#d1fae5', marginBottom: '1rem' }}>
                  Status: <strong>{subscription.status}</strong>
                </p>
                
                {renderUsageDisplay()}
              </div>
              
              <Button 
                onClick={handleManageSubscription}
                variant="ghost"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white',
                  border: '1px solid white',
                  backdropFilter: 'blur(10px)'
                }}
              >
                Manage Subscription
              </Button>
            </div>
          </Card>
        )}

        {/* Pricing Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '2rem', 
          marginBottom: '4rem' 
        }}>
          {plans.map((plan) => (
            <Card
              key={plan.planType}
              style={{
                padding: '2rem',
                position: 'relative',
                border: currentPlan === plan.planType ? '3px solid #059669' : '1px solid #e5e7eb',
                transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                boxShadow: plan.popular 
                  ? '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
                  : '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
            >
              {plan.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  padding: '0.375rem 1.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  letterSpacing: '0.05em',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                  BEST VALUE
                </div>
              )}

              {currentPlan === plan.planType && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  right: '1rem',
                  background: '#059669',
                  color: 'white',
                  padding: '0.375rem 1rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                  CURRENT
                </div>
              )}

              {/* Plan Header */}
              <div style={{ 
                background: plan.gradient, 
                color: 'white', 
                borderRadius: '0.75rem', 
                padding: '1.5rem',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ marginBottom: '0.5rem' }}>{plan.icon}</div>
                <h2 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  {plan.name}
                </h2>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.25rem' }}>
                  <span style={{ fontSize: '3rem', fontWeight: 'bold' }}>${plan.price}</span>
                  <span style={{ fontSize: '1rem', opacity: 0.9 }}>{plan.period}</span>
                </div>
              </div>

              <p style={{ 
                fontSize: '0.875rem', 
                color: '#6b7280', 
                textAlign: 'center', 
                marginBottom: '1.5rem',
                minHeight: '2.5rem'
              }}>
                {plan.description}
              </p>

              {/* Features List */}
              <ul style={{ marginBottom: '2rem', listStyle: 'none', padding: 0 }}>
                {plan.features.map((feature, idx) => (
                  <li 
                    key={idx}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.875rem'
                    }}
                  >
                    <div style={{ 
                      flexShrink: 0,
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: feature.included ? '#d1fae5' : '#fee2e2',
                      marginTop: '2px'
                    }}>
                      {feature.included ? (
                        <Check size={14} style={{ color: '#059669' }} />
                      ) : (
                        <X size={14} style={{ color: '#dc2626' }} />
                      )}
                    </div>
                    <span style={{ 
                      color: feature.included ? '#1f2937' : '#9ca3af',
                      fontWeight: feature.highlight ? '700' : '400'
                    }}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              {currentPlan === plan.planType ? (
                <Button 
                  variant="secondary"
                  style={{ width: '100%', cursor: 'not-allowed', opacity: 0.6 }}
                  disabled
                >
                  Current Plan
                </Button>
              ) : (
                <Button 
                  onClick={() => plan.planType === 'free' ? handleManageSubscription() : handleUpgrade(plan.planType)}
                  variant="primary"
                  style={{ 
                    width: '100%',
                    background: plan.gradient,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                  disabled={processingPlan === plan.planType}
                >
                  {processingPlan === plan.planType ? (
                    <>
                      <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Processing...
                    </>
                  ) : (
                    <>
                      {currentPlan === 'free' ? 'Upgrade' : 
                       plan.planType === 'free' ? 'Downgrade' :
                       plans.findIndex(p => p.planType === currentPlan) > plans.findIndex(p => p.planType === plan.planType) ? 
                       'Downgrade' : 'Upgrade'}
                      <ArrowRight size={16} />
                    </>
                  )}
                </Button>
              )}
            </Card>
          ))}
        </div>

        {/* FAQ Section */}
        <Card style={{ padding: '2rem' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center', marginBottom: '2rem', color: '#1f2937' }}>
            Frequently Asked Questions
          </h2>
          
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {faqs.map((faq, idx) => (
              <div 
                key={idx}
                style={{ 
                  borderBottom: idx < faqs.length - 1 ? '1px solid #e5e7eb' : 'none',
                  paddingBottom: '1.5rem',
                  marginBottom: '1.5rem'
                }}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    textAlign: 'left'
                  }}
                >
                  <h3 style={{ 
                    fontSize: '1.125rem', 
                    fontWeight: '600', 
                    color: '#1f2937',
                    margin: 0
                  }}>
                    {faq.question}
                  </h3>
                  <ArrowRight 
                    size={20} 
                    style={{ 
                      color: '#6b7280',
                      transform: expandedFaq === idx ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }} 
                  />
                </button>
                {expandedFaq === idx && (
                  <p style={{ 
                    color: '#6b7280', 
                    marginTop: '0.75rem',
                    fontSize: '0.9375rem',
                    lineHeight: '1.6'
                  }}>
                    {faq.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};